import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  Settings2,
  ShieldCheck,
  LogOut,
  Menu,
  ArrowLeft,
  Gem,
  Lock,
  Sliders,
  LifeBuoy,
  ClipboardCheck,
  Handshake,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testid: "admin-nav-overview", end: true },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "admin-nav-customers" },
  { to: "/admin/operations", label: "Operations", icon: Sliders, testid: "admin-nav-operations" },
  { to: "/admin/support", label: "Support tickets", icon: LifeBuoy, testid: "admin-nav-support" },
  { to: "/admin/affiliates", label: "Affiliates", icon: Handshake, testid: "admin-nav-affiliates" },
  { to: "/admin/platform-setup", label: "Platform Setup", icon: Settings2, testid: "admin-nav-setup" },
  { to: "/admin/meta-checklist", label: "Meta App Review", icon: ClipboardCheck, testid: "admin-nav-meta-checklist" },
  { to: "/admin/linkedin-checklist", label: "LinkedIn setup", icon: ClipboardCheck, testid: "admin-nav-linkedin-checklist" },
];

function SidebarBody({ onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const lockAdmin = async () => {
    try {
      await api.post("/admin/lock");
      toast.success("Admin mode locked");
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (_e) {
      toast.error("Could not lock admin");
    }
  };

  return (
    <div className="flex h-full w-full flex-col relative" data-testid="admin-sidebar">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(420px 200px at 30% 0%, rgba(255,138,61,0.18), transparent 70%)",
        }}
      />
      <div className="relative flex items-center gap-2 px-5 py-4 border-b border-[color:var(--border-subtle)]">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[color:var(--accent-warm)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] shadow-[0_4px_18px_rgba(255,138,61,0.45)]">
          <ShieldCheck className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-lg font-semibold tracking-tight leading-none">
            Admin Console
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--accent-warm)] mt-1">
            Owner only · private
          </div>
        </div>
      </div>
      <nav className="relative flex-1 px-3 py-4 space-y-1">
        {ADMIN_NAV.map((item) => {
          const Icon = item.icon;
          const active = item.end
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={() => onNavigate?.()}
              className={
                "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-all " +
                (active
                  ? "bg-[color:var(--surface-2)] text-[color:var(--text-1)] shadow-[inset_0_0_0_1px_rgba(255,138,61,0.40)]"
                  : "text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--surface-1)]")
              }
            >
              <Icon className={"h-4 w-4 " + (active ? "text-[color:var(--accent-warm)]" : "")} />
              <span>{item.label}</span>
              {active ? (
                <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[color:var(--accent-warm)]" />
              ) : null}
            </Link>
          );
        })}
        <div className="hairline my-3" />
        <Link
          to="/dashboard"
          onClick={() => onNavigate?.()}
          className="group flex items-center gap-3 rounded-md px-3 py-2 text-sm text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--surface-1)]"
          data-testid="admin-nav-customer-dashboard"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Customer dashboard</span>
        </Link>
      </nav>
      <div className="relative border-t border-[color:var(--border-subtle)] p-3 space-y-2">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[color:var(--accent-warm)] to-[color:var(--brand-secondary)] flex items-center justify-center text-xs font-semibold text-[color:var(--bg-0)]">
            <Gem className="h-3.5 w-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[color:var(--text-1)] truncate">
              {user?.name || "Admin"}
            </div>
            <div className="text-xs text-[color:var(--text-3)] truncate">{user?.email}</div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={lockAdmin}
          className="w-full border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] text-[color:var(--accent-warm)] hover:bg-[color:rgba(255,138,61,0.12)]"
          data-testid="admin-lock"
        >
          <Lock className="h-4 w-4 mr-2" /> Lock admin & exit
        </Button>
      </div>
    </div>
  );
}

export default function AdminShell({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] flex">
      <aside
        className="hidden md:flex md:w-[272px] md:flex-col fixed inset-y-0 left-0 bg-[color:var(--bg-1)] border-r border-[color:var(--border-subtle)] overflow-hidden"
        data-testid="admin-sidebar-desktop"
      >
        <SidebarBody />
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[color:var(--bg-1)]/95 backdrop-blur border-b border-[color:var(--border-subtle)] px-3 h-14 flex items-center gap-2">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="admin-mobile-menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="p-0 w-[280px] bg-[color:var(--bg-1)] border-r border-[color:var(--border-subtle)]"
          >
            <SidebarBody onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-[color:var(--accent-warm)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)]">
            <ShieldCheck className="h-3 w-3" />
          </div>
          <span className="font-display text-base font-semibold">Admin Console</span>
        </div>
      </div>

      <main className="flex-1 md:ml-[272px] pt-14 md:pt-0 min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ type: "spring", stiffness: 420, damping: 34, mass: 0.9 }}
          className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 max-w-[1320px] mx-auto"
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
}
